import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import { Box, Button, DialogActions, DialogContent, Grid, MenuItem, TextField } from '@mui/material';
import AutoSearch, { Option } from 'components/common/AutoSearch';
import countries from 'config/countries';
import phones from 'config/phones';
import { ModalActionProps } from 'models/AutoModals';
import { PartyModel } from 'models/PartyModel';
import useParties from 'modules/parties/hooks/useParties';
import auth from 'recoil/atoms/auth';


type PartyProps = {
    party: PartyModel,
    create?: boolean,
    emails?: string[],
} & ModalActionProps<{ party: PartyModel, action: 'attach' | 'create' }>

const PartyDialog = ({party, onFinish, emails, create}: PartyProps) => {
    const my = useRecoilValue(auth);
    const {t} = useTranslation();
    const [showForm, setShowForm] = useState(!create);
    const [state, setState] = useState(party);
    const [found, setFound] = useState<Option | null>();
    const {findParty} = useParties();

    const submit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (showForm) {
            if (emails && state.email && emails.includes(state.email))
                return; // don't add more than once

            if (!state.email && !state.phone && !state.companyName && !state.lastName && !state.companyCode)
                return; // don't send empty form

            onFinish({
                action: 'create',
                party: state,
            });
        } else if (found) {
            onFinish({
                action: 'attach',
                party: {
                    id: found.id,
                } as PartyModel,
            });
        }
    };

    const pasteFullName = (e: React.ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData('text');
        const split = pasted.split(' ');

        if (split.length > 1) {
            setState(old => ({
                ...old,
                lastName: split[0],
                firstName: split[1],
                middleName: split[2],
            }));
        } else {
            setState(old => ({
                ...old,
                lastName: pasted,
            }));
        }
    };

    const change = (e: React.ChangeEvent<HTMLInputElement>) => {
        setState(old => ({...old, [e.target.name]: e.target.value}));
    };

    const changeCountry = (e: React.ChangeEvent<HTMLInputElement>) => {
        setState(old => ({
            ...old,
            country: e.target.value,
            phone: old.phone.length < 8 ? `+${phones[e.target.value]}` : old.phone,
        }));
    };

    return (
        <Box component="form" onSubmit={submit}>
            <DialogContent>
                {showForm ? (
                    <Grid container spacing={2} mb={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                id="email"
                                name="email"
                                label={t('email_address')}
                                type="email"
                                fullWidth
                                value={state.email || ''}
                                onChange={change}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                id="phone"
                                name="phone"
                                label={t('phone')}
                                type="tel"
                                fullWidth
                                value={state.phone || `+${phones[state.country]}`}
                                onChange={change}
                            />
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <TextField
                                id="lastName"
                                name="lastName"
                                label={t('last_name')}
                                fullWidth
                                value={state.lastName || ''}
                                onChange={change}
                                onPaste={pasteFullName}
                            />
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <TextField
                                id="firstName"
                                name="firstName"
                                label={t('first_name')}
                                fullWidth
                                value={state.firstName || ''}
                                onChange={change}
                            />
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <TextField
                                id="middleName"
                                name="middleName"
                                label={t('middle_name')}
                                fullWidth
                                value={state.middleName || ''}
                                onChange={change}
                            />
                        </Grid>

                        <Grid item xs={4}>
                            <TextField
                                id="companyName"
                                name="companyName"
                                label={t('company_name')}
                                fullWidth
                                value={state.companyName || ''}
                                onChange={change}
                            />
                        </Grid>

                        <Grid item xs={4}>
                            <TextField
                                id="companyCode"
                                name="companyCode"
                                label={t('company_code')}
                                fullWidth
                                value={state.companyCode || ''}
                                onChange={change}
                            />
                        </Grid>

                        <Grid item xs={4}>
                            <TextField
                                id="tin"
                                name="tin"
                                label={t('tin')}
                                fullWidth
                                value={state.tin || ''}
                                onChange={change}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                id="alias"
                                name="alias"
                                label={t('alias')}
                                fullWidth
                                value={state.alias || ''}
                                onChange={change}
                            />
                        </Grid>

                        <Grid item xs={6}>
                            <TextField
                                id="country"
                                name="country"
                                label={t('country')}
                                fullWidth
                                select
                                value={state.country}
                                onChange={changeCountry}
                            >
                                {Object.entries(countries).map(([c, n]) => <MenuItem key={c} value={c}>{n}</MenuItem>)}
                            </TextField>
                        </Grid>

                        <Grid item xs={6}>
                            <TextField
                                id="city"
                                name="city"
                                label={t('city')}
                                fullWidth
                                value={state.city || ''}
                                onChange={change}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                id="address"
                                name="address"
                                label={t('address')}
                                fullWidth
                                value={state.address || ''}
                                onChange={change}
                            />
                        </Grid>
                    </Grid>
                ) : (
                    <>
                        <AutoSearch
                            search={s => findParty(s, state.type)}
                            onChange={r => setFound(r)}
                            label={t('search')}
                            keepValue
                        />

                        <Box
                            sx={{
                                textAlign: 'center',

                                '& .or': {
                                    '&::before, &::after': {
                                        content: '""',
                                        display: 'inline-block',
                                        width: 20,
                                        height: 5,
                                        borderTop: '1px solid #666666',
                                        margin: '0 20px',
                                    }
                                },
                            }}
                        >
                            <div className="or">{t('or')}</div>
                            <Button onClick={() => setShowForm(true)}>{t('create')}</Button>
                        </Box>
                    </>
                )}
            </DialogContent>

            {create && my.permissions.has(`parties create ${state.type}`) &&
                <DialogActions>
                    <Button type="submit">{t('add')}</Button>
                </DialogActions>
            }

            {!create && my.permissions.has(`parties update ${state.type}`) &&
                <DialogActions>
                    <Button type="submit">{t('save')}</Button>
                </DialogActions>
            }
        </Box>
    );
};

export default PartyDialog;
